import { ancanaApi } from "./ancanaApi";
import {
  IBroadcast,
  IBroadcastPreview,
  IBroadcastRecipient,
  IDeliveryStatus,
  IRendered,
  ISyncReport,
  IWhatsAppTemplate,
  RecipientStatus,
} from "../types/models/broadcasts";

/**
 * WhatsApp broadcasts.
 *
 * `preview` and `importList` post the same file to two different endpoints on
 * purpose — the first reads it and reports, the second stores it. That split is
 * the whole feature: it is what lets someone see "381 rows, 40 with no phone"
 * and decide, rather than importing and then undoing.
 */

const multipart = (fields: Record<string, any>) => {
  const body = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    body.append(key, value instanceof File ? value : String(value));
  });
  return {
    body,
    config: { headers: { "Content-Type": "multipart/form-data" } },
  };
};

export interface IRecipientQuery {
  broadcast: number;
  status?: RecipientStatus;
  exclude_reason?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

export interface IPaginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const broadcastsApi = {
  list: (params?: Record<string, any>) =>
    ancanaApi
      .get<IPaginated<IBroadcast>>("broadcasts/", { params })
      .then((r) => r.data),

  get: (id: number) =>
    ancanaApi.get<IBroadcast>(`broadcasts/${id}/`).then((r) => r.data),

  /** Read a CSV and report what is in it. Stores nothing. */
  preview: (file: File, columns: { name_column?: string; phone_column?: string } = {}) => {
    const { body, config } = multipart({ file, ...columns });
    return ancanaApi
      .post<IBroadcastPreview>("broadcasts/preview/", body, config)
      .then((r) => r.data);
  },

  /** The list lands under the acting operator (`X-Operator`, see ancanaApi). */
  importList: (
    file: File,
    name: string,
    columns: { name_column?: string; phone_column?: string } = {}
  ) => {
    const { body, config } = multipart({ file, name, ...columns });
    return ancanaApi
      .post<IBroadcast>("broadcasts/import/", body, config)
      .then((r) => r.data);
  },

  update: (id: number, patch: Partial<IBroadcast>) =>
    ancanaApi.patch<IBroadcast>(`broadcasts/${id}/`, patch).then((r) => r.data),

  /** Attach a template and move the list out of draft. */
  ready: (id: number, template: number, variable_map: Record<string, string> = {}) =>
    ancanaApi
      .post<IBroadcast>(`broadcasts/${id}/ready/`, { template, variable_map })
      .then((r) => r.data),

  /** What a handful of real recipients would actually receive. */
  rendered: (id: number) =>
    ancanaApi.get<IRendered>(`broadcasts/${id}/rendered/`).then((r) => r.data),

  /** Hands the run to a worker. Returns immediately; poll the broadcast. */
  send: (id: number) =>
    ancanaApi.post<IBroadcast>(`broadcasts/${id}/send/`, {}).then((r) => r.data),

  pause: (id: number) =>
    ancanaApi.post<IBroadcast>(`broadcasts/${id}/pause/`, {}).then((r) => r.data),

  /** Stops for good. Already-sent recipients stay sent — there is no unsending. */
  cancel: (id: number) =>
    ancanaApi.post<IBroadcast>(`broadcasts/${id}/cancel/`, {}).then((r) => r.data),

  deliveryStatus: () =>
    ancanaApi
      .get<IDeliveryStatus>("broadcasts/delivery-status/")
      .then((r) => r.data),

  recipients: (params: IRecipientQuery) =>
    ancanaApi
      .get<IPaginated<IBroadcastRecipient>>("broadcast-recipients/", { params })
      .then((r) => r.data),

  /**
   * Edit a row in place. A changed `phone_raw` is re-normalised server-side and
   * the row moves buckets, so the response is authoritative — never merge a
   * local guess at the new status over it.
   */
  updateRecipient: (id: number, patch: { phone_raw?: string; greeting?: string }) =>
    ancanaApi
      .patch<IBroadcastRecipient>(`broadcast-recipients/${id}/`, patch)
      .then((r) => r.data),

  /** Send to this one contact now — the point of putting your own number in. */
  testSend: (id: number) =>
    ancanaApi
      .post<IBroadcastRecipient>(`broadcast-recipients/${id}/test-send/`, {})
      .then((r) => r.data),

  excludeRecipient: (id: number) =>
    ancanaApi
      .post<IBroadcastRecipient>(`broadcast-recipients/${id}/exclude/`, {})
      .then((r) => r.data),

  /** Answers 409 when the row still cannot be sent to; the caller shows why. */
  includeRecipient: (id: number) =>
    ancanaApi
      .post<IBroadcastRecipient>(`broadcast-recipients/${id}/include/`, {})
      .then((r) => r.data),
};

/**
 * The local mirror of Meta's message templates.
 *
 * Read-only apart from `sync`, which is a pull: templates are authored and
 * approved in the WhatsApp Manager, and editing the copy here would only make
 * it disagree with what customers actually receive.
 */
export const whatsappTemplatesApi = {
  list: (params?: Record<string, any>) =>
    ancanaApi
      .get<IPaginated<IWhatsAppTemplate>>("whatsapp-templates/", {
        // Pinned high because the API's default PAGE_SIZE is 3, and a picker
        // that silently shows the first three templates is worse than none.
        params: { page_size: 200, ...params },
      })
      .then((r) => r.data.results ?? []),

  sync: (operator?: number) =>
    ancanaApi
      .post<ISyncReport>("whatsapp-templates/sync/", operator ? { operator } : {})
      .then((r) => r.data),
};
